(() => {
  /**
   * @param {HTMLElement} root
   */
  function init(root) {
    const grid = root.querySelector('[data-expand-grid]');
    if (!grid) return;
    if (grid.hasAttribute('data-expand-init')) return;
    grid.setAttribute('data-expand-init', 'true');

    const cards = Array.from(grid.querySelectorAll('[data-expand-card]')).filter(
      (el) => el instanceof HTMLElement
    );
    if (!cards.length) return;

    // Free-scroll slider behavior (mobile layout): mouse/pen drag with kinetic/inertial scrolling.
    // Touch devices keep native scrolling (already swipeable + momentum).
    (() => {
      let isPointerDown = false;
      let isDragging = false;
      let pointerId = null;
      let startX = 0;
      let startScrollLeft = 0;
      let lastX = 0;
      let lastT = 0;
      let scrollVelocity = 0; // px per ms (scrollLeft direction)
      let suppressClickUntil = 0;
      let inertiaRaf = 0;
      let prevScrollBehavior = '';

      const DRAG_THRESHOLD_PX = 5;
      const STOP_VELOCITY = 0.02; // px/ms
      const FRICTION_PER_FRAME = 0.92; // tuned for ~60fps

      function isMobile() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      function isScrollableX() {
        return grid.scrollWidth > grid.clientWidth + 1;
      }

      function stopInertia() {
        if (inertiaRaf) cancelAnimationFrame(inertiaRaf);
        inertiaRaf = 0;
      }

      function startInertia(v0) {
        stopInertia();
        let v = Number.isFinite(v0) ? v0 : 0;
        let last = performance.now();

        function frame(now) {
          const dt = Math.min(32, now - last);
          last = now;

          const max = Math.max(0, grid.scrollWidth - grid.clientWidth);
          grid.scrollLeft = grid.scrollLeft + v * dt;

          // Clamp at edges (no "bounce" to keep it predictable)
          if (grid.scrollLeft < 0) {
            grid.scrollLeft = 0;
            v = 0;
          } else if (grid.scrollLeft > max) {
            grid.scrollLeft = max;
            v = 0;
          }

          // Time-adjusted decay (so it feels consistent across frame rates)
          const decay = Math.pow(FRICTION_PER_FRAME, dt / 16);
          v *= decay;

          if (Math.abs(v) < STOP_VELOCITY) {
            inertiaRaf = 0;
            return;
          }

          inertiaRaf = requestAnimationFrame(frame);
        }

        inertiaRaf = requestAnimationFrame(frame);
      }

      grid.addEventListener(
        'click',
        (e) => {
          if (performance.now() < suppressClickUntil) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );

      grid.addEventListener('dragstart', (e) => {
        if (isScrollableX()) e.preventDefault();
      });

      function shouldHandlePointer(e) {
        if (!isMobile()) return false;
        if (!isScrollableX()) return false;
        if (e.pointerType === 'touch') return false;
        if (typeof e.button === 'number' && e.button !== 0) return false;
        return true;
      }

      grid.addEventListener('pointerdown', (e) => {
        if (!shouldHandlePointer(e)) return;

        stopInertia();

        isPointerDown = true;
        isDragging = false;
        pointerId = e.pointerId;
        startX = e.clientX;
        startScrollLeft = grid.scrollLeft;
        lastX = e.clientX;
        lastT = performance.now();
        scrollVelocity = 0;
        grid.classList.add('is-pointer-down');

        // Disable smooth scrolling during drag for 1:1 tracking
        prevScrollBehavior = grid.style.scrollBehavior || '';
        grid.style.scrollBehavior = 'auto';

        try {
          grid.setPointerCapture(e.pointerId);
        } catch (_) {
          // ignore
        }
      });

      grid.addEventListener('pointermove', (e) => {
        if (!isPointerDown) return;
        if (pointerId !== null && e.pointerId !== pointerId) return;

        const now = performance.now();
        const dx = e.clientX - startX;

        const dt = Math.max(1, now - lastT);
        // Pointer velocity (px/ms). Scroll direction is the inverse.
        scrollVelocity = -(e.clientX - lastX) / dt;
        lastX = e.clientX;
        lastT = now;

        if (!isDragging && Math.abs(dx) >= DRAG_THRESHOLD_PX) {
          isDragging = true;
          grid.classList.add('is-dragging');
        }

        if (!isDragging) return;
        e.preventDefault();
        grid.scrollLeft = startScrollLeft - dx;
      });

      function endDrag(e) {
        if (!isPointerDown) return;
        if (pointerId !== null && e.pointerId !== pointerId) return;

        const now = performance.now();
        const wasDragging = isDragging;

        isPointerDown = false;
        isDragging = false;
        pointerId = null;
        grid.classList.remove('is-pointer-down');
        grid.classList.remove('is-dragging');
        grid.style.scrollBehavior = prevScrollBehavior;

        if (wasDragging) {
          suppressClickUntil = now + 450;
          startInertia(scrollVelocity);
        }

        try {
          grid.releasePointerCapture(e.pointerId);
        } catch (_) {
          // ignore
        }
      }

      grid.addEventListener('pointerup', endDrag);
      grid.addEventListener('pointercancel', endDrag);
      grid.addEventListener('pointerleave', (e) => {
        if (isPointerDown) endDrag(e);
      });
    })();

    function getColsEffective() {
      const styles = getComputedStyle(root);
      const desktop = parseInt(styles.getPropertyValue('--custom-expand-grid-cols-desktop-effective').trim(), 10);
      const tablet = parseInt(styles.getPropertyValue('--custom-expand-grid-cols-tablet-effective').trim(), 10);
      const mobile = parseInt(styles.getPropertyValue('--custom-expand-grid-cols-mobile-effective').trim(), 10);

      if (window.matchMedia('(max-width: 749px)').matches) return Number.isFinite(mobile) && mobile > 0 ? mobile : 1;
      if (window.matchMedia('(max-width: 989px)').matches) return Number.isFinite(tablet) && tablet > 0 ? tablet : 1;
      return Number.isFinite(desktop) && desktop > 0 ? desktop : 1;
    }

    function getGapPx() {
      const cs = getComputedStyle(grid);
      const gap = cs.columnGap || cs.gap || '0px';
      const value = parseFloat(gap);
      return Number.isFinite(value) ? value : 0;
    }

    function layout() {
      const cols = Math.max(1, Math.min(getColsEffective(), cards.length));
      const gapPx = getGapPx();
      const containerWidth = grid.clientWidth;
      if (!containerWidth) return;

      // Tablet/mobile: no "widening" behavior. Let CSS grid handle layout safely.
      if (window.matchMedia('(max-width: 989px)').matches) {
        cards.forEach((c) => {
          c.style.flexBasis = '';
          c.style.maxWidth = '';
        });
        return;
      }

      const active = cards.find((c) => c.classList.contains('is-active')) || null;

      // Group into rows by desired columns (not by current offsetTop),
      // otherwise an initial 100%-width layout would permanently "lock" into single column.
      for (let start = 0; start < cards.length; start += cols) {
        const rowCards = cards.slice(start, start + cols);
        const itemsInRow = rowCards.length;
        const availablePx = containerWidth - gapPx * (itemsInRow - 1);
        if (availablePx <= 0) return;

        const hasActive = !!active && rowCards.includes(active);

        if (hasActive && itemsInRow > 1) {
          const inactiveWeight = 198;
          const activeWeight = 327;
          const totalWeight = activeWeight + (itemsInRow - 1) * inactiveWeight;
          const inactivePx = (availablePx * inactiveWeight) / totalWeight;
          const activePx = (availablePx * activeWeight) / totalWeight;

          rowCards.forEach((c) => {
            const w = c === active ? activePx : inactivePx;
            c.style.flexBasis = `${w}px`;
            c.style.maxWidth = `${w}px`;
          });
        } else {
          // No active in this row (or only 1 item): distribute evenly.
          const w = availablePx / itemsInRow;
          rowCards.forEach((c) => {
            c.style.flexBasis = `${w}px`;
            c.style.maxWidth = `${w}px`;
          });
        }
      }
    }

    function setActive(target) {
      cards.forEach((card, idx) => {
        const isActive = card === target;
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      layout();
      // Mobile: ensure the activated card is visible in the horizontal scroller
      if (window.matchMedia('(max-width: 749px)').matches) {
        try {
          target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        } catch (_) {
          // no-op for older browsers
        }
      }
    }

    // Default: first card active
    setActive(cards[0]);

    cards.forEach((card) => {
      card.addEventListener('click', () => setActive(card));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setActive(card);
        }
      });
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'button');
    });

    let raf = 0;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(layout);
    });

    // Ensure correct sizing on initial paint
    requestAnimationFrame(layout);
  }

  function initAll() {
    document.querySelectorAll('.custom-expand-grid').forEach((el) => {
      if (el instanceof HTMLElement) init(el);
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', (e) => {
    const target = /** @type {HTMLElement | null} */ (e.target);
    if (!target) return;
    const root = target.querySelector('.custom-expand-grid');
    if (root instanceof HTMLElement) init(root);
  });
})();

